import { Phone, Mail } from "lucide-react";
import LazyMap from "@/components/LazyMap";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import FAQ from "@/components/FAQ";
import ProductCard from "@/components/ProductCard";
import MagicBentoGrid from "@/components/MagicBentoGrid";
import LanguageToggle from "@/components/LanguageToggle";
import SEOHead from "@/components/SEOHead";
import ScrollExpandHero from "@/components/ui/scroll-expansion-hero";
import { FadeIn } from "@/hooks/use-scroll-animation";
import heroImage from "@/assets/hero-livingroom.png?format=webp&w=1200";
import bgImage from "@/assets/lasirullaverho-2.png?format=webp&w=1920";
import blindsangled from "@/assets/blindsangled.png?format=webp&w=900";
import solarLogo from "@/assets/solar-logo.png?format=webp&w=200";
import osioLogo from "@/assets/osio-logo.webp";
import kaihtimet from "@/assets/kaihtimet.png?format=webp&w=800";
import lasirullaverho from "@/assets/lasirullaverho.png?format=webp&w=800";
import rullaverho from "@/assets/rullaverho.png?format=webp&w=800";
import lamelliverho from "@/assets/lamelliverho.png?format=webp&w=800";
import verhokiskot from "@/assets/verhokiskot.png?format=webp&w=800";

const products = [
  {
    title: "Blinds",
    description: "Blackout & smart blinds, motorized blinds and mosquito nets – professionally installed.",
    image: kaihtimet,
    href: "/en/blind-installation",
  },
  {
    title: "Balcony blinds & roller blinds",
    description: "Balcony glazing blinds and roller blinds provide sun protection and privacy. Motorized and manual models.",
    image: lasirullaverho,
    href: "/en/balcony-installation",
  },
  {
    title: "Roller blinds",
    description: "Roller blinds are a modern and stylish solution for window covering and interior design.",
    image: rullaverho,
    href: "/en/roller-blind-installation",
  },
  {
    title: "Vertical blinds",
    description: "Vertical blinds are ideal for large windows and sliding doors. Easy light adjustment.",
    image: lamelliverho,
    href: "/en/vertical-blind-installation",
  },
  {
    title: "Curtain tracks & accessories",
    description: "High-quality curtain tracks and installation accessories. Track and rod systems for all needs.",
    image: verhokiskot,
    href: "/en/curtain-track-installation",
  },
];

const IndexEn = () => {
  return (
    <>
      <SEOHead
        title="Keski-Uudenmaan Kaihdin | Blind & Curtain Installation"
        description="Professional blind, roller blind, vertical blind and curtain track installation in Central Uusimaa. Serving Hyvinkää, Järvenpää, Kerava, Mäntsälä, Nurmijärvi, Pornainen and Tuusula."
        path="/en/"
        alternatePath="/"
        lang="en"
      />
      <Header />
      <main id="main-content">
        {/* Hero */}
        <ScrollExpandHero
          innerImageSrc={heroImage}
          bgImageSrc={bgImage}
          innerImageAlt="Beautiful blinds in a living room"
          bgImageAlt="Balcony roller blinds"
          staticContent={<LanguageToggle />}
          slideLeftContent={
            <>
              <h1 className="font-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
                Professional blind installation
              </h1>
              <div className="mt-8 mb-6 hidden md:flex items-center gap-3 justify-center">
                <span className="text-white/70 text-sm">Official reseller:</span>
                <a href="https://solar.fi" target="_blank" rel="noopener noreferrer" className="bg-white rounded px-3 py-1.5">
                  <img src={solarLogo} alt="Solar Kaihdin logo" className="h-5" />
                </a>
                <a href="https://osio.fi" target="_blank" rel="noopener noreferrer" className="bg-white rounded px-3 py-1.5">
                  <img src={osioLogo} alt="Osio logo" className="h-5" />
                </a>
              </div>
            </>
          }
          slideRightContent={
            <>
              <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-8">
                We install blinds, balcony blinds & roller blinds, roller blinds, vertical blinds and curtain tracks in the Central Uusimaa region.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:0405839991" className="inline-flex items-center justify-center gap-2 bg-white text-primary px-6 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity">
                  <Phone className="w-4 h-4" /> Call: 040 583 9991
                </a>
                <button onClick={() => { window.dispatchEvent(new CustomEvent('hero-skip')); setTimeout(() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }, 400); }} className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-6 py-3 rounded-md font-semibold hover:bg-white hover:text-primary transition-colors">
                  Request a quote
                </button>
              </div>
              <div className="mt-6 flex md:hidden items-center gap-3 justify-center">
                <span className="text-white/70 text-sm">Official reseller:</span>
                <a href="https://solar.fi" target="_blank" rel="noopener noreferrer" className="bg-white rounded px-3 py-1.5">
                  <img src={solarLogo} alt="Solar Kaihdin logo" className="h-5" />
                </a>
                <a href="https://osio.fi" target="_blank" rel="noopener noreferrer" className="bg-white rounded px-3 py-1.5">
                  <img src={osioLogo} alt="Osio logo" className="h-5" />
                </a>
              </div>
            </>
          }
        />

        {/* About */}
        <section id="about" className="py-20" style={{ backgroundColor: '#f7f6f4' }}>
          <div className="container max-w-6xl">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="text-left">
                <FadeIn>
                  <h2 className="font-display text-3xl md:text-4xl font-bold mb-5">Professional installation service</h2>
                </FadeIn>
                <FadeIn delay={150}>
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    Keski-Uudenmaan Kaihdin specializes in the professional installation of blinds, curtains and sun protection products. 
                    We are an official reseller of <a href="https://solar.fi" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-accent-foreground">Solar Kaihdin</a> and{" "}
                    <a href="https://osio.fi" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-accent-foreground">Osio</a> products, 
                    serving the entire Central Uusimaa region. Whether it's a single window or a whole house – we handle measuring, ordering and installation as a turnkey service.
                  </p>
                </FadeIn>
              </div>
              <FadeIn delay={300}>
                <img src={blindsangled} alt="Blinds" className="w-full max-w-md mx-auto" />
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Products */}
        <section id="products" className="py-20 bg-secondary">
          <div className="container">
            <FadeIn>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-3">Products & services</h2>
              <p className="text-center text-muted-foreground mb-10">
                We install high-quality products from Solar Kaihdin and Osio.
              </p>
            </FadeIn>
            <MagicBentoGrid className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <FadeIn key={p.href} delay={i * 100}>
                  <ProductCard {...p} />
                </FadeIn>
              ))}
            </MagicBentoGrid>
          </div>
        </section>

        {/* FAQ */}
        <FAQ />

        {/* Contact */}
        <ContactForm />

        {/* Map */}
        <section className="py-12 bg-background">
          <div className="container">
            <h2 className="font-display text-2xl font-bold text-center mb-6">Find us</h2>
            <LazyMap
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1953.5!2d25.0544!3d60.5539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x468df0a7c5555555%3A0x0!2sIltalenkki+23%2C+05400+Jokela!5e0!3m2!1sen!2sfi!4v1700000000000"
              title="Keski-Uudenmaan Kaihdin location on map"
            />
          </div>
        </section>

        {/* Company info bar */}
        <section className="py-10 bg-muted">
          <div className="container">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center">
                <span className="font-semibold text-foreground">Tmi Teppo Honkanen</span>
                <span>Business ID: 2303843-1</span>
                <span>Iltalenkki 23, 05400 Jokela</span>
              </div>
              <a href="tel:0405839991" className="inline-flex items-center gap-2 font-semibold text-foreground">
                <Phone className="w-4 h-4" /> 040 583 9991
              </a>
              <a href="mailto:info@ku-kaihdin.fi" className="inline-flex items-center gap-2 font-semibold text-foreground">
                <Mail className="w-4 h-4" /> info@ku-kaihdin.fi
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default IndexEn;
